/**
 * EZTree 程序化樹木生成器（從 @dgreenheck/ez-tree 移植為 TS）
 *
 * 只負責生成樹幹與樹葉的 BufferGeometry，不建立材質。
 * 材質由外部（PlacedObjects.tsx）根據渲染風格決定。
 */

import * as THREE from 'three'
import RNG from './rng'
import { Branch } from './branch'
import { Billboard, TreeType } from './enums'
import TreeOptions from './options'

import oakMediumPreset from './presets/oak_medium.json'
import pineMediumPreset from './presets/pine_medium.json'
import ashMediumPreset from './presets/ash_medium.json'
import willowMediumPreset from './presets/willow_medium.json'
import aspenMediumPreset from './presets/aspen_medium.json'
import bush1Preset from './presets/bush_1.json'
import crabclawPreset from './presets/crabclaw.json'
import broadDotPreset from './presets/broad_dot.json'

export const PRESETS = {
  oak_medium: oakMediumPreset,
  pine_medium: pineMediumPreset,
  ash_medium: ashMediumPreset,
  willow_medium: willowMediumPreset,
  aspen_medium: aspenMediumPreset,
  bush_1: bush1Preset,
  crabclaw: crabclawPreset,
  broad_dot: broadDotPreset,
} as const

export type PresetName = keyof typeof PRESETS

interface GeometryData {
  verts: number[]
  normals: number[]
  indices: number[]
  uvs: number[]
}

interface BranchSection {
  origin: THREE.Vector3
  orientation: THREE.Euler
  radius: number
}

/**
 * 生成樹木的幾何體（樹幹 + 樹葉）
 * 回傳 { branchGeometry, leafGeometry }
 */
export function generateTreeGeometry(
  options: TreeOptions,
): { branchGeometry: THREE.BufferGeometry; leafGeometry: THREE.BufferGeometry } {
  const rng = new RNG(options.seed)
  const branchQueue: Branch[] = []

  const branches: GeometryData = { verts: [], normals: [], indices: [], uvs: [] }
  const leaves: GeometryData = { verts: [], normals: [], indices: [], uvs: [] }

  // ── 各種工具函數 ──

  function generateBranchIndices(indexOffset: number, branch: Branch) {
    const N = branch.segmentCount + 1
    for (let i = 0; i < branch.sectionCount; i++) {
      for (let j = 0; j < branch.segmentCount; j++) {
        const v1 = indexOffset + i * N + j
        const v2 = indexOffset + i * N + (j + 1)
        const v3 = v1 + N
        const v4 = v2 + N
        branches.indices.push(v1, v3, v2, v2, v3, v4)
      }
    }
  }

  function generateLeaf(origin: THREE.Vector3, orientation: THREE.Euler) {
    let i = leaves.verts.length / 3
    const leafSize =
      options.leaves.size *
      (1 + rng.random(options.leaves.sizeVariance, -options.leaves.sizeVariance))
    const W = leafSize
    const L = leafSize

    const createLeaf = (rotation: number) => {
      const v = [
        new THREE.Vector3(-W / 2, L, 0),
        new THREE.Vector3(-W / 2, 0, 0),
        new THREE.Vector3(W / 2, 0, 0),
        new THREE.Vector3(W / 2, L, 0),
      ].map((v) =>
        v
          .applyEuler(new THREE.Euler(0, rotation, 0))
          .applyEuler(orientation)
          .add(origin),
      )

      leaves.verts.push(
        v[0].x, v[0].y, v[0].z,
        v[1].x, v[1].y, v[1].z,
        v[2].x, v[2].y, v[2].z,
        v[3].x, v[3].y, v[3].z,
      )

      const n = new THREE.Vector3(0, 0, 1).applyEuler(orientation)
      leaves.normals.push(
        n.x, n.y, n.z,
        n.x, n.y, n.z,
        n.x, n.y, n.z,
        n.x, n.y, n.z,
      )
      leaves.uvs.push(0, 1, 0, 0, 1, 0, 1, 1)
      leaves.indices.push(i, i + 1, i + 2, i, i + 2, i + 3)
      i += 4
    }

    createLeaf(0)
    if (options.leaves.billboard === Billboard.Double) {
      createLeaf(Math.PI / 2)
    }
  }

  function generateLeaves(sections: BranchSection[]) {
    const radialOffset = rng.random()
    for (let i = 0; i < options.leaves.count; i++) {
      const leafStart = rng.random(1.0, options.leaves.start)
      const sectionIndex = Math.floor(leafStart * (sections.length - 1))
      const sectionA = sections[sectionIndex]
      const sectionB = sectionIndex === sections.length - 1
        ? sectionA
        : sections[sectionIndex + 1]

      const alpha =
        (leafStart - sectionIndex / (sections.length - 1)) /
        (1 / (sections.length - 1))

      const leafOrigin = new THREE.Vector3().lerpVectors(sectionA.origin, sectionB.origin, alpha)

      const qA = new THREE.Quaternion().setFromEuler(sectionA.orientation)
      const qB = new THREE.Quaternion().setFromEuler(sectionB.orientation)
      const parentOrientation = new THREE.Euler().setFromQuaternion(qB.slerp(qA, alpha))

      const radialAngle = 2.0 * Math.PI * (radialOffset + i / options.leaves.count)
      const q1 = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(1, 0, 0),
        options.leaves.angle / (180 / Math.PI),
      )
      const q2 = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), radialAngle)
      const q3 = new THREE.Quaternion().setFromEuler(parentOrientation)
      const leafOrientation = new THREE.Euler().setFromQuaternion(q3.multiply(q2.multiply(q1)))

      generateLeaf(leafOrigin, leafOrientation)
    }
  }

  function generateChildBranches(count: number, level: number, sections: BranchSection[]) {
    const radialOffset = rng.random()
    for (let i = 0; i < count; i++) {
      const childBranchStart = rng.random(1.0, options.branch.start[level])
      const sectionIndex = Math.floor(childBranchStart * (sections.length - 1))
      const sectionA = sections[sectionIndex]
      const sectionB = sectionIndex === sections.length - 1
        ? sectionA
        : sections[sectionIndex + 1]

      const alpha =
        (childBranchStart - sectionIndex / (sections.length - 1)) /
        (1 / (sections.length - 1))

      const childBranchOrigin = new THREE.Vector3().lerpVectors(
        sectionA.origin, sectionB.origin, alpha,
      )

      const childBranchRadius =
        options.branch.radius[level] *
        ((1 - alpha) * sectionA.radius + alpha * sectionB.radius)

      const qA = new THREE.Quaternion().setFromEuler(sectionA.orientation)
      const qB = new THREE.Quaternion().setFromEuler(sectionB.orientation)
      const parentOrientation = new THREE.Euler().setFromQuaternion(qB.slerp(qA, alpha))

      const radialAngle = 2.0 * Math.PI * (radialOffset + i / count)
      const q1 = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(1, 0, 0),
        options.branch.angle[level] / (180 / Math.PI),
      )
      const q2 = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), radialAngle)
      const q3 = new THREE.Quaternion().setFromEuler(parentOrientation)
      const childBranchOrientation = new THREE.Euler().setFromQuaternion(
        q3.multiply(q2.multiply(q1)),
      )

      const childBranchLength =
        options.branch.length[level] *
        (options.type === TreeType.Evergreen ? 1.0 - childBranchStart : 1.0)

      branchQueue.push(
        new Branch(
          childBranchOrigin,
          childBranchOrientation,
          childBranchLength,
          childBranchRadius,
          level,
          options.branch.sections[level],
          options.branch.segments[level],
        ),
      )
    }
  }

  function generateBranch(branch: Branch) {
    const indexOffset = branches.verts.length / 3
    let sectionOrientation = branch.orientation.clone()
    let sectionOrigin = branch.origin.clone()
    const sectionLength =
      branch.length /
      branch.sectionCount /
      (options.type === 'deciduous' ? options.branch.levels - 1 : 1)

    const sections: BranchSection[] = []

    for (let i = 0; i <= branch.sectionCount; i++) {
      let sectionRadius = branch.radius
      if (i === branch.sectionCount && branch.level === options.branch.levels) {
        sectionRadius = 0.001
      } else if (options.type === TreeType.Deciduous) {
        sectionRadius *= 1 - options.branch.taper[branch.level] * (i / branch.sectionCount)
      } else if (options.type === TreeType.Evergreen) {
        sectionRadius *= 1 - (i / branch.sectionCount)
      }

      let first: { vertex: THREE.Vector3; normal: THREE.Vector3; uv: THREE.Vector2 } | null = null
      for (let j = 0; j < branch.segmentCount; j++) {
        const angle = (2.0 * Math.PI * j) / branch.segmentCount
        const vertex = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))
          .multiplyScalar(sectionRadius)
          .applyEuler(sectionOrientation)
          .add(sectionOrigin)
        const normal = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))
          .applyEuler(sectionOrientation)
          .normalize()
        const uvVal = new THREE.Vector2(j / branch.segmentCount, (i % 2 === 0) ? 0 : 1)

        branches.verts.push(vertex.x, vertex.y, vertex.z)
        branches.normals.push(normal.x, normal.y, normal.z)
        branches.uvs.push(uvVal.x, uvVal.y)

        if (j === 0) first = { vertex, normal, uv: uvVal }
      }

      // 重複第一個頂點確保 UV 連續
      if (first) {
        branches.verts.push(first.vertex.x, first.vertex.y, first.vertex.z)
        branches.normals.push(first.normal.x, first.normal.y, first.normal.z)
        branches.uvs.push(1, first.uv.y)
      }

      sections.push({
        origin: sectionOrigin.clone(),
        orientation: sectionOrientation.clone(),
        radius: sectionRadius,
      })

      sectionOrigin.add(
        new THREE.Vector3(0, sectionLength, 0).applyEuler(sectionOrientation),
      )

      // 隨機擾動
      const gnarliness =
        Math.max(1, 1 / Math.sqrt(sectionRadius)) *
        options.branch.gnarliness[branch.level]
      sectionOrientation.x += rng.random(gnarliness, -gnarliness)
      sectionOrientation.z += rng.random(gnarliness, -gnarliness)

      // 生長力
      const qSection = new THREE.Quaternion().setFromEuler(sectionOrientation)
      const qTwist = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 1, 0),
        options.branch.twist[branch.level],
      )
      const qForce = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        new THREE.Vector3().copy(options.branch.force.direction as THREE.Vector3).normalize(),
      )
      qSection.multiply(qTwist)
      qSection.rotateTowards(qForce, options.branch.force.strength / sectionRadius)
      sectionOrientation.setFromQuaternion(qSection)
    }

    generateBranchIndices(indexOffset, branch)

    // 落葉樹的終端分支
    if (options.type === 'deciduous') {
      const lastSection = sections[sections.length - 1]
      if (branch.level < options.branch.levels) {
        branchQueue.push(
          new Branch(
            lastSection.origin,
            lastSection.orientation,
            options.branch.length[branch.level + 1],
            lastSection.radius,
            branch.level + 1,
            branch.sectionCount,
            branch.segmentCount,
          ),
        )
      } else {
        generateLeaf(lastSection.origin, lastSection.orientation)
      }
    }

    if (branch.level === options.branch.levels) {
      generateLeaves(sections)
    } else if (branch.level < options.branch.levels) {
      generateChildBranches(
        options.branch.children[branch.level],
        branch.level + 1,
        sections,
      )
    }
  }

  // ── 開始生成 ──

  branchQueue.push(
    new Branch(
      new THREE.Vector3(),
      new THREE.Euler(),
      options.branch.length[0],
      options.branch.radius[0],
      0,
      options.branch.sections[0],
      options.branch.segments[0],
    ),
  )

  while (branchQueue.length > 0) {
    const branch = branchQueue.shift()!
    generateBranch(branch)
  }

  // ── 構建 BufferGeometry ──

  const branchGeometry = new THREE.BufferGeometry()
  branchGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(branches.verts), 3))
  branchGeometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(branches.normals), 3))
  branchGeometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(branches.uvs), 2))
  if (branches.indices.length > 65535) {
    branchGeometry.setIndex(new THREE.BufferAttribute(new Uint32Array(branches.indices), 1))
  } else {
    branchGeometry.setIndex(new THREE.BufferAttribute(new Uint16Array(branches.indices), 1))
  }
  branchGeometry.computeBoundingSphere()

  const leafGeometry = new THREE.BufferGeometry()
  if (leaves.verts.length > 0) {
    leafGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(leaves.verts), 3))
    leafGeometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(leaves.uvs), 2))
    if (leaves.indices.length > 65535) {
      leafGeometry.setIndex(new THREE.BufferAttribute(new Uint32Array(leaves.indices), 1))
    } else {
      leafGeometry.setIndex(new THREE.BufferAttribute(new Uint16Array(leaves.indices), 1))
    }
    leafGeometry.computeVertexNormals()
    leafGeometry.computeBoundingSphere()
  }

  return { branchGeometry, leafGeometry }
}

/** 用預設名稱直接生成樹木幾何體 */
export function generateTreeFromPreset(presetName: PresetName, seedOverride?: number) {
  const opts = new TreeOptions()
  const presetData = structuredClone(PRESETS[presetName])
  opts.copy(presetData)
  if (seedOverride !== undefined) {
    opts.seed = seedOverride
  }
  return generateTreeGeometry(opts)
}
